import React, {
  createContext, useCallback, useContext, useMemo, useState, ReactNode, Dispatch, SetStateAction,
} from 'react';
import {
  getFormattedMatchedValues,
  getDedupedNewSearchResults,
  formatIncludedLocationTargets,
  formatExcludedLocationTargets,
} from './utils/format-bulk-locations';

interface Location {
  Id: number;
  ExpandedName: string;
  Type: string;
  CountryCode: string;
  LesLocationType: string;
  LocationStatus: string;
  EnglishName: string;
  GeoPoint: {
    Latitude: number;
    Longitude: number;
  };
}

interface LocationTargetEntry {
  location: Location;
  targetingType: 'Included' | 'Excluded';
  radiusSize: number | null;
  radiusUnit: string | null;
  errors: string[];
}

interface SearchResult {
  SearchTerm: string;
  Location: Location | null;
}

interface MatchedLocation {
  target: {
    name: string;
    isTargeted: boolean;
    Location: Location;
    SearchTerm: string;
  };
}

interface BulkLocationsSearchContextType {
  searchResults: SearchResult[] | null;
  countryFilter: string | null;
  error: string;
  inputText: string;
  isDrawerOpen: boolean;
  isLoading: boolean;
  isEnterLocationsOpen: boolean;
  matchedLocations: MatchedLocation[];
  unmatchedLocations: string[];
  locationTargets: LocationTargetEntry[];
  negativeLocationTargets: LocationTargetEntry[];
  campaignApiToken: string | undefined;
  bingMapsApiKey: string;
  campaignApiServiceRoot: string | undefined;
  currentSearchCounts: { searchTerms: number; unmatchedLocations: number };
  setSearchResults: Dispatch<SetStateAction<SearchResult[]>>;
  setSearchTerms: Dispatch<SetStateAction<string[]>>;
  setCountryFilter: Dispatch<SetStateAction<string>>;
  setError: Dispatch<SetStateAction<string>>;
  setInputText: Dispatch<SetStateAction<string>>;
  setIsEnterLocationsOpen: Dispatch<SetStateAction<boolean>>;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  onBulkLocationsSearch: (searchTerms: string[], countryFilter: string) => Promise<{ value: SearchResult[] }>;
  handleOnAddLocations: () => void;
  handleCountrySelect: (event: any, data: { optionValue: string }) => void;
  setMatchedLocations: Dispatch<SetStateAction<MatchedLocation[]>>;
  handleEnterLocationsClose: () => void;
  handleBulkSearch: () => void;
  handlePanelClose: () => void;
  mapRef: React.RefObject<any> | ((instance: any | null) => void);
  mapId: string;
}

interface BulkLocationsSearchProviderProps {
  /**
   * Callback function that submits location selections back to the consumer of the Bulk Location Search component
   */
  onAddLocations: (data: { includedLocations: LocationTargetEntry[]; excludedLocations: LocationTargetEntry[] }) => void;
  /**
   * Bulk Locations Search odata API callback function
   */
  onBulkLocationsSearch: (searchTerms: string[], countryFilter: string) => Promise<{ value: SearchResult[] }>;
  /**
   * Bulk Locations Search drawer open state
   */
  isDrawerOpen?: boolean;
  /**
   * Bulk Locations Search drawer open state callback
   */
  setIsDrawerOpen?: Dispatch<SetStateAction<boolean>>;
  /**
   * Bulk Locations Search children components
   */
  children: ReactNode;
  /**
   * Ref that can be used to trigger actions in the map web control like zooming.
   */
  mapRef: React.RefObject<any> | ((instance: any | null) => void);
  /**
   * CCMTSmallToken used for odata requests.
   */
  campaignApiToken?: string;
  /**
   * API key for use with Bing Maps
   */
  bingMapsApiKey: string;
  /**
   * Campaign odata API URL.
   */
  campaignApiServiceRoot?: string;
  /**
   * An optional ID string for BingMapsView, needed when multiple Bing Maps are on the same page.
   */
  mapId?: string;
}

export const BulkLocationsSearchContext = createContext<BulkLocationsSearchContextType>({
  searchResults: null,
  countryFilter: null,
  error: '',
  inputText: '',
  isDrawerOpen: false,
  isLoading: false,
  isEnterLocationsOpen: true,
  matchedLocations: [],
  unmatchedLocations: [],
  locationTargets: [],
  negativeLocationTargets: [],
  campaignApiToken: undefined,
  bingMapsApiKey: '',
  campaignApiServiceRoot: undefined,
  currentSearchCounts: { searchTerms: 0, unmatchedLocations: 0 },
  setSearchResults: () => {},
  setSearchTerms: () => {},
  setCountryFilter: () => {},
  setError: () => {},
  setInputText: () => {},
  setIsEnterLocationsOpen: () => {},
  setIsDrawerOpen: () => {},
  onBulkLocationsSearch: () => Promise.resolve({ value: [] }),
  handleOnAddLocations: () => {},
  handleCountrySelect: () => {},
  setMatchedLocations: () => {},
  handleEnterLocationsClose: () => {},
  handleBulkSearch: () => {},
  handlePanelClose: () => {},
  mapRef: () => {},
  mapId: 'bulk-locations-search-bing-map',
});

export const useBulkLocationsSearchContext = () => useContext(BulkLocationsSearchContext);

const BulkLocationsSearchProvider: React.FC<BulkLocationsSearchProviderProps> = (props) => {
  const {
    onAddLocations,
    children,
    onBulkLocationsSearch,
    mapRef,
    isDrawerOpen = false,
    setIsDrawerOpen,
    campaignApiToken,
    bingMapsApiKey,
    campaignApiServiceRoot,
    mapId = 'bulk-locations-search-bing-map',
  } = props;

  const [isEnterLocationsOpen, setIsEnterLocationsOpen] = useState(true);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [countryFilter, setCountryFilter] = useState('');
  const [error, setError] = useState('');
  const [inputText, setInputText] = useState('');
  const [matchedLocations, setMatchedLocations] = useState<MatchedLocation[]>([]);
  const [unmatchedLocations, setUnmatchedLocations] = useState<string[]>([]);
  const [currentSearchCounts, setCurrentSearchCounts] = useState({ searchTerms: 0, unmatchedLocations: 0 });

  const locationTargets: LocationTargetEntry[] = useMemo(
    () => formatIncludedLocationTargets(matchedLocations.filter((loc) => loc.target.isTargeted === true)),
    [matchedLocations],
  );

  const negativeLocationTargets: LocationTargetEntry[] = useMemo(
    () => formatExcludedLocationTargets(matchedLocations.filter((loc) => loc.target.isTargeted === false)),
    [matchedLocations],
  );

  const handleEnterLocationsClose = useCallback(() => {
    setIsEnterLocationsOpen(false);
    setError('');
    setInputText('');
    setCountryFilter('');
  }, []);

  const handlePanelClose = useCallback(() => {
    handleEnterLocationsClose();
    setSearchResults([]);
    if (setIsDrawerOpen) {
      setIsDrawerOpen(false);
    }
    setIsEnterLocationsOpen(true);
    setCurrentSearchCounts({ searchTerms: 0, unmatchedLocations: 0 });
    setMatchedLocations([]);
    setUnmatchedLocations([]);
  }, [handleEnterLocationsClose, setIsDrawerOpen]);

  const handleOnAddLocations = useCallback(() => {
    if ((locationTargets.length > 0 || negativeLocationTargets.length > 0) && typeof onAddLocations === 'function') {
      onAddLocations({ includedLocations: locationTargets, excludedLocations: negativeLocationTargets });
      handlePanelClose();
    }
  }, [handlePanelClose, locationTargets, negativeLocationTargets, onAddLocations]);

  const handleCountrySelect = useCallback((_, data: { optionValue: string }) => {
    setCountryFilter(data.optionValue);
  }, [setCountryFilter]);

  const handleBulkSearch = useCallback(() => {
    if (inputText.length === 0) { return; }
    setIsLoading(true);

    onBulkLocationsSearch(searchTerms, countryFilter)
      .then((results) => {
        const newResults = results.value;
        const nullLocationCount = newResults.filter((item) => item.Location === null).length;
        setCurrentSearchCounts({ searchTerms: searchTerms.length, unmatchedLocations: nullLocationCount });

        const dedupedNewResults = getDedupedNewSearchResults(newResults, matchedLocations, unmatchedLocations);
        setSearchResults((prevSearchResults) => [...prevSearchResults, ...dedupedNewResults]);

        const matchedNewResults = dedupedNewResults.filter((r) => r.Location !== null);
        const formattedMatchedLocations = getFormattedMatchedValues(matchedNewResults);
        setMatchedLocations((prev) => [...prev, ...formattedMatchedLocations]);

        const unmatchedNewResults = dedupedNewResults.filter((r) => r.Location === null);
        setUnmatchedLocations((prev) => [...prev, ...unmatchedNewResults.map((r) => r.SearchTerm)]);

        handleEnterLocationsClose();
      })
      .catch((err) => {
        console.log('Error during bulk locations search:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [countryFilter, handleEnterLocationsClose, inputText.length, matchedLocations, onBulkLocationsSearch, searchTerms, unmatchedLocations]);

  const context = useMemo(() => ({
    isEnterLocationsOpen,
    isDrawerOpen,
    searchResults,
    countryFilter,
    error,
    inputText,
    matchedLocations,
    unmatchedLocations,
    locationTargets,
    negativeLocationTargets,
    campaignApiToken,
    bingMapsApiKey,
    campaignApiServiceRoot,
    currentSearchCounts,
    isLoading,
    setIsEnterLocationsOpen,
    setIsDrawerOpen,
    setSearchResults,
    setSearchTerms,
    setCountryFilter,
    setError,
    setInputText,
    handleOnAddLocations,
    onBulkLocationsSearch,
    setMatchedLocations,
    handleCountrySelect,
    handleEnterLocationsClose,
    handleBulkSearch,
    handlePanelClose,
    mapRef,
    mapId,
  }), [
    isEnterLocationsOpen,
    isDrawerOpen,
    searchResults,
    countryFilter,
    error,
    inputText,
    matchedLocations,
    unmatchedLocations,
    locationTargets,
    negativeLocationTargets,
    campaignApiToken,
    bingMapsApiKey,
    campaignApiServiceRoot,
    currentSearchCounts,
    isLoading,
    setIsEnterLocationsOpen,
    setIsDrawerOpen,
    setSearchResults,
    setSearchTerms,
    setCountryFilter,
    setError,
    setInputText,
    handleOnAddLocations,
    onBulkLocationsSearch,
    setMatchedLocations,
    handleCountrySelect,
    handleEnterLocationsClose,
    handleBulkSearch,
    handlePanelClose,
    mapRef,
    mapId,
  ]);

  return (
    <BulkLocationsSearchContext.Provider value={context}>
      {children}
    </BulkLocationsSearchContext.Provider>
  );
};

export default BulkLocationsSearchProvider;
